/**
 * Feature-flag context object and `useFeatures` hook.
 *
 * `.ts` (no JSX) so `FeaturesProvider.tsx` stays a Fast Refresh component boundary.
 */

import { createContext, useContext } from 'react';

export interface FeaturesContextType {
  /** Whether the flower catalog feature is enabled in this environment. */
  isFlowerCatalogEnabled: boolean;
  /** True only when flower catalog is enabled AND at least one active product exists. */
  hasFlowerProducts: boolean;
  /** Whether the mailed-card catalog feature is enabled in this environment. */
  isMailedCardCatalogEnabled: boolean;
  /** True only when mailed-card catalog is enabled AND writable artwork exists. */
  hasMailedCardArtworks: boolean;
  /** When true, My Account offers the assistant-order approval choices. */
  isAgentOrdersEnabled: boolean;
  /** Public /ai page, use-case docs, and the AI nav/footer link. */
  isPublicAiPageEnabled: boolean;
  /** True when the Goody gift catalog stage is Active (customer shop surfaces). */
  isGiftCatalogEnabled: boolean;
  /** True after the first /api/features response (or a failed fetch that fell back to defaults). */
  featuresResolved: boolean;
  /** Force-refresh feature flags (e.g. after catalog sync). */
  refreshFeatures: () => Promise<void>;
}

/**
 * React context object. Exported for `FeaturesProvider` only — consumers use `useFeatures`.
 */
export const FeaturesContext = createContext<FeaturesContextType | undefined>(undefined);

/**
 * Hook to access feature flag context.
 * Must be used within a FeaturesProvider.
 */
export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};
